import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchPopularRemarks, fetchRemarkSuggestions } from '../api/remarkSuggestions';
import { trackRemarkSuggestionUsage } from '../services/savedRemarks';

export interface RemarkSuggestion {
  id: number;
  title: string;
  content: string;
  price: number | null;
  usageCount: number;
  score: number;
  confidence: number;
  matchReasons: string[];
  createdAt: string;
  updatedAt: string;
}

export interface RemarkSuggestionsResult {
  suggestions: RemarkSuggestion[];
  itemFeatures?: {
    normalizedName: string;
    brand: string | null;
    category: string | null;
    modelNumber: string | null;
    keywords: string[];
  };
  error?: string;
}

export interface UseRemarkSuggestionsOptions {
  autoFetch?: boolean; // Automatically fetch when item changes
  minConfidence?: number; // Minimum confidence score to show (0-100)
}

export function useRemarkSuggestions(
  item: {
    item_name?: string;
    model_number?: string;
    brand?: string;
    item_type?: string;
  } | null,
  options: UseRemarkSuggestionsOptions = {}
) {
  const { autoFetch = true, minConfidence = 10 } = options;
  
  const [suggestions, setSuggestions] = useState<RemarkSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itemFeatures, setItemFeatures] = useState<RemarkSuggestionsResult['itemFeatures']>();

  const fetchSuggestions = useCallback(async () => {
    if (!item || !item.item_name) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetchRemarkSuggestions(item);

      if (response.data.error) {
        setError(response.data.error);
        setSuggestions([]);
      } else {
        // Filter by minimum confidence
        const filteredSuggestions = response.data.suggestions.filter(
          s => s.confidence >= minConfidence
        );
        
        setSuggestions(filteredSuggestions);
        setItemFeatures(response.data.itemFeatures);
      }
    } catch (err) {
      console.error('Error fetching remark suggestions:', err);
      setError('Failed to fetch remark suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [item, minConfidence]);

  // Ref to always have latest fetchSuggestions without adding it to effect deps
  const fetchSuggestionsRef = useRef(fetchSuggestions);
  fetchSuggestionsRef.current = fetchSuggestions;

  const trackUsage = useCallback(async (
    savedRemarkId: number,
    rfqItemId: number | undefined,
    wasAccepted: boolean
  ) => {
    try {
      await trackRemarkSuggestionUsage(savedRemarkId, rfqItemId, wasAccepted);
    } catch (err) {
      console.error('Error tracking remark usage:', err);
    }
  }, []);

  const applySuggestion = useCallback((
    suggestion: RemarkSuggestion,
    rfqItemId: number | undefined,
    onApply: (content: string, price?: number) => void
  ) => {
    // Track that this suggestion was accepted
    trackUsage(suggestion.id, rfqItemId, true);
    
    // Apply the suggestion
    onApply(suggestion.content, suggestion.price || undefined);
  }, [trackUsage]);

  // Auto-fetch when item changes
  useEffect(() => {
    if (autoFetch && item?.item_name) {
      const debounceTimer = setTimeout(() => {
        fetchSuggestionsRef.current();
      }, 300); // Debounce to avoid too many requests

      return () => clearTimeout(debounceTimer);
    }
  }, [item?.item_name, item?.model_number, autoFetch]);

  return {
    suggestions,
    loading,
    error,
    itemFeatures,
    fetchSuggestions,
    trackUsage,
    applySuggestion,
    hasSuggestions: suggestions.length > 0
  };
}

/**
 * Fetch the most popular saved remarks (by usage count).
 * Used as a fallback when item-specific suggestions are unavailable.
 */
export function usePopularRemarks(limit = 10) {
  const [remarks, setRemarks] = useState<RemarkSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPopular = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchPopularRemarks(limit);
      setRemarks(response.data?.remarks || []);
    } catch (err) {
      console.error('Error fetching popular remarks:', err);
      setError('Failed to fetch popular remarks');
      setRemarks([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchPopular();
  }, [fetchPopular]);

  return {
    remarks,
    loading,
    error,
    refetch: fetchPopular,
  };
}

